import {EVENT} from './bridge/constants';
import {destroyElement, toCSS} from './bridge/props';
import {CLASS} from './enums';

export type ContainerConfig = {
  background?: string;
  overflow?: string;
  borderRadius?: string;
  fadeOnRender?: boolean;
  allowTransparency?: boolean;
  modal?: boolean;
};

export function createContainerTemplate(config: ContainerConfig = {}) {
  const {
    background = '#fff',
    overflow,
    borderRadius,
    fadeOnRender = true,
    allowTransparency = false,
    modal = false
  } = config;

  return function containerTemplateFn({
    uid,
    frame,
    placeholder,
    doc,
    props,
    event,
    dimensions: {width, height}
  }: Record<any, any>): HTMLElement | undefined {
    if (!frame) {
      return;
    }

    if (allowTransparency) {
      frame.setAttribute('allowtransparency', 'true');
      frame.style.background = 'transparent';
    }

    const div = doc.createElement('div');
    div.setAttribute('id', uid);
    const style = doc.createElement('style');
    if (props?.cspNonce) {
      style.setAttribute('nonce', props.cspNonce);
    }

    if (modal) {
      const backdrop = doc.createElement('div');
      backdrop.classList.add('backdrop');

      style.appendChild(
        doc.createTextNode(`
        #${uid} {
            position: fixed;
            inset: 0;
            z-index: 99999;
        }
        #${uid} > .backdrop {
            position: fixed;
            inset: 0;
            background-color: rgba(40, 40, 40, 0.7);
            opacity: 0;
            transition: opacity 0.35s ease-in-out;
            will-change: opacity;
        }
        #${uid} > .backdrop.${CLASS.VISIBLE} {
            opacity: 1;
        }
        #${uid} > iframe {
            display: block;
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            transition: opacity .2s ease-in-out;
        }
        #${uid} > iframe.${CLASS.INVISIBLE} {
            opacity: 0;
        }
        #${uid} > iframe.${CLASS.VISIBLE} {
            opacity: 1;
        }
      `)
      );

      div.appendChild(backdrop);
      div.appendChild(frame);
      if (placeholder) {
        div.appendChild(placeholder);
      }
      div.appendChild(style);

      event.on(EVENT.DISPLAY, () => {
        void backdrop.offsetHeight;
        requestAnimationFrame(() => backdrop.classList.add(CLASS.VISIBLE));
      });

      event.on(EVENT.BEFORE_CLOSE, () => {
        backdrop.classList.remove(CLASS.VISIBLE);
      });

      // Modal always hides iframe until RENDERED (backdrop covers the page)
      frame.classList.add(CLASS.INVISIBLE);
      if (fadeOnRender && placeholder) {
        placeholder.classList.add(CLASS.VISIBLE);
      }

      event.on(EVENT.RENDERED, () => {
        if (fadeOnRender) {
          if (placeholder) {
            placeholder.classList.remove(CLASS.VISIBLE);
            placeholder.classList.add(CLASS.INVISIBLE);
          }
          frame.classList.remove(CLASS.INVISIBLE);
          frame.classList.add(CLASS.VISIBLE);
          setTimeout(() => destroyElement(placeholder), 1);
        } else {
          if (placeholder) destroyElement(placeholder);
          frame.style.transition = 'none';
          frame.classList.remove(CLASS.INVISIBLE);
          frame.classList.add(CLASS.VISIBLE);
        }
      });

      // No RESIZE handler — fullscreen modal uses inset: 0
    } else {
      const innerStyle = props?.style || props?.innerStyle;
      const initialHeight =
        height === '0'
          ? '0'
          : toCSS(
              innerStyle?.base?.height || innerStyle?.height || (height === 'auto' ? 45 : height)
            );

      const containerStyles = [
        'display: block',
        'position: relative',
        `width: ${width}`,
        `height: ${allowTransparency ? height : initialHeight}`,
        `background: ${background}`
      ];
      if (overflow) {
        containerStyles.push(`overflow: ${overflow}`);
      }
      if (borderRadius) {
        containerStyles.push(`border-radius: ${borderRadius}`);
      }

      style.appendChild(
        doc.createTextNode(`
        #${uid} {
            ${containerStyles.join(';\n          ')};
        }
        #${uid} > iframe {
            display: block;
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            transition: opacity .2s ease-in-out;
        }
        #${uid} > iframe.${CLASS.INVISIBLE} {
            opacity: 0;
        }
        #${uid} > iframe.${CLASS.VISIBLE} {
            opacity: 1;
        }
      `)
      );

      div.appendChild(frame);
      if (placeholder) {
        div.appendChild(placeholder);
      }
      div.appendChild(style);

      event.on(EVENT.RESIZE, ({width: newWidth, height: newHeight}: Record<any, any>) => {
        if (typeof newWidth === 'number') {
          div.style.width = toCSS(newWidth);
        }

        if (typeof newHeight === 'number') {
          div.style.height = toCSS(newHeight);
        }
      });

      // Only hide iframe when there's a placeholder to crossfade with.
      // Without a placeholder, the iframe starts visible — child apps hide their
      // own body (display:none) until ready, and parent components use their own
      // loading overlays (e.g., React Skeleton). This eliminates the timing gap
      // between skeleton removal (onLoad) and iframe visibility (RENDERED).
      if (placeholder) {
        frame.classList.add(CLASS.INVISIBLE);
        if (fadeOnRender) {
          placeholder.classList.add(CLASS.VISIBLE);
        }

        event.on(EVENT.RENDERED, () => {
          if (fadeOnRender) {
            placeholder.classList.remove(CLASS.VISIBLE);
            placeholder.classList.add(CLASS.INVISIBLE);
            frame.classList.remove(CLASS.INVISIBLE);
            frame.classList.add(CLASS.VISIBLE);
            setTimeout(() => destroyElement(placeholder), 1);
          } else {
            destroyElement(placeholder);
            frame.style.transition = 'none';
            frame.classList.remove(CLASS.INVISIBLE);
            frame.classList.add(CLASS.VISIBLE);
          }
        });
      }
    }

    return div;
  };
}

export const containerTemplate = createContainerTemplate({
  overflow: 'hidden',
  borderRadius: 'var(--monei-radius, 4px)'
});

export const modalContainerTemplate = createContainerTemplate({modal: true});
